
import React, { useState } from "react";
import { ArrowRight } from "lucide-react";
import { PhoneInput } from "@/components/PhoneInput";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const Hero: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("1"); // Default to US/Canada
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleCallMe = async () => {
    if (!phoneNumber) return;
    
    setIsSubmitting(true);
    
    try {
      // Format the full phone number with country code
      const fullPhoneNumber = `+${countryCode}${phoneNumber}`;
      
      console.log("Initiating call to:", fullPhoneNumber);
      
      // Call our edge function to log the call request
      const { data, error } = await supabase.functions.invoke('initiate-call', {
        body: {
          to_number: fullPhoneNumber,
          first_message: "Hello, this is HeyVail calling. I'd like to discuss how unifying all of your customer convesations and automating followup can scale your business but not your headcount."
        }
      });

      console.log("Edge function response:", data, error);

      if (error) {
        console.error("Error initiating call:", error);
        toast({
          title: "Error",
          description: "There was a problem initiating your call. Please try again.",
          variant: "destructive"
        });
        return;
      }

      // Success path
      toast({
        title: "Call Requested",
        description: "We'll call you shortly!",
      });

      // Reset phone number after successful submission
      setPhoneNumber("");
    } catch (error) {
      console.error("Error submitting call request:", error);
      toast({
        title: "Error",
        description: "There was a problem processing your request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center mb-16 md:mb-24">
          <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium bg-primary/10 text-primary rounded-full opacity-0 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            Turn AI Promises Into Revenue
          </span>
          
          <h1 className="mb-6 text-5xl md:text-6xl font-light text-foreground opacity-0 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            Conversations
            <br />grow your business.
            <br /><span className="font-normal">HeyVail scales them.</span>
          </h1>
          
          <p className="mx-auto max-w-2xl text-2xl md:text-3xl text-muted-foreground mb-10 opacity-0 animate-fade-in" style={{ animationDelay: '0.6s' }}>
            Imagine a sales and customer service team that always follows the playbook, works 24/7, and covers email, calls, SMS, and website chat. Quit imagining and meet Vail, your full service answer to creating and scaling conversations. Drop your number in and she'll give you a call right away.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 opacity-0 animate-fade-in" style={{ animationDelay: '0.8s' }}>
            <div className="w-full sm:w-auto flex-1 max-w-md">
              <PhoneInput
                value={phoneNumber}
                onChange={setPhoneNumber}
                countryCode={countryCode}
                onCountryCodeChange={setCountryCode}
                className="w-full"
                disabled={isSubmitting}
                onSubmit={handleCallMe}
              />
            </div>
            <button
              onClick={handleCallMe}
              className="primary-button whitespace-nowrap flex items-center justify-center gap-2 w-full sm:w-auto"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Processing..." : (
                <>Call Me Now <ArrowRight className="h-4 w-4" /></>
              )}
            </button>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-muted-foreground text-sm opacity-0 animate-fade-in" style={{ animationDelay: '1s' }}>
              By submitting, you agree to our{" "}
              <a href="/terms-of-service" className="text-primary hover:underline">Terms of Service</a>
              {" "}and{" "}
              <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>
            </p>
          </div>
        </div>

        <div className="relative mx-auto max-w-5xl opacity-0 animate-fade-in" style={{ animationDelay: '1s' }}>
          <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-elevated">
            <img 
              src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=1200&q=80" 
              alt="Business professional using AI platform" 
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="absolute -bottom-12 -right-6 md:-bottom-16 md:-right-16 w-1/2 max-w-sm">
            <div className="glass-card p-4 md:p-6 animate-float">
              <div className="flex items-start gap-4">
                <div className="text-primary font-semibold text-xl md:text-2xl">3x</div>
                <div>
                  <h4 className="text-foreground font-medium text-sm md:text-base">more leads</h4>
                  <p className="text-muted-foreground text-xs md:text-sm">when you use AI to communicate at scale</p>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute -top-8 -left-6 md:-top-14 md:-left-14 w-1/2 max-w-sm">
            <div className="glass-card p-4 md:p-6 animate-float" style={{ animationDelay: '1s' }}>
              <div className="flex items-start gap-4">
                <div className="text-primary font-semibold text-xl md:text-2xl">Weeks</div>
                <div>
                  <h4 className="text-foreground font-medium text-sm md:text-base">to see results</h4>
                  <p className="text-muted-foreground text-xs md:text-sm">not months of trial and error</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden md:block absolute top-1/4 right-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl -z-10"></div>
      <div className="hidden md:block absolute bottom-1/4 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -z-10"></div>
    </section>
  );
};
